import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { SplitHandle } from '../SplitHandle'

import type { RowGroup } from './GroupedSections'

// Rail drag bounds: never let either side collapse past a usable width.
const RAIL_MIN_PX = 112
const RAIL_DEFAULT_PX = 160
const PANE_MIN_PX = 240

/**
 * Master-detail presentation of grouped rows: the group titles live in a LEFT rail (label + count,
 * one selected), and the selected group's rows fill the RIGHT pane. A **draggable divider** between
 * them resizes the split (same pointer-capture pattern as the page's panel/map handle). The two
 * columns scroll independently and together fill the card height. A drop-in alternative to
 * `GroupedSections` (same `groups` + `renderGroup` contract) for boards that want a split instead of
 * stacked collapsibles. The right pane declares `@container/list` so the content inside it (gallery
 * grid / list rows) still sizes to its own — now narrower — width.
 *
 * The active group survives a `groups` change when its key still exists (e.g. a filter tweak that
 * keeps the group); otherwise it falls back to the first group so the right pane is never blank.
 */
export function GroupMasterDetail<T>({ groups, renderGroup }: {
  groups: RowGroup<T>[]
  renderGroup: (rows: T[]) => ReactNode
}) {
  const [activeKey, setActiveKey] = useState<string | null>(groups[0]?.key ?? null)
  const [railWidth, setRailWidth] = useState(RAIL_DEFAULT_PX)
  // The flex row the drag measures against — captured once per drag (its width is stable while the
  // rail resizes, so no need to re-measure on every move).
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setActiveKey((prev) =>
      prev !== null && groups.some((g) => g.key === prev) ? prev : (groups[0]?.key ?? null),
    )
  }, [groups])

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const row = rowRef.current
    if (row === null) {
      return
    }
    const rect = row.getBoundingClientRect()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const onMove = (ev: PointerEvent) => {
      const w = Math.min(rect.width - PANE_MIN_PX, Math.max(RAIL_MIN_PX, ev.clientX - rect.left))
      setRailWidth(w)
    }
    const cleanup = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', cleanup)
      handle.removeEventListener('pointercancel', cleanup)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', cleanup)
    handle.addEventListener('pointercancel', cleanup)
  }, [])

  const active = groups.find((g) => g.key === activeKey) ?? groups[0] ?? null
  if (active === null) {
    return null
  }

  return (
    <div className="min-h-0 flex-1 p-2">
      <div ref={rowRef} className="flex h-full min-h-0">
        {/* Left rail — the group titles. */}
        <nav
          className="min-h-0 flex-none space-y-[3px] overflow-y-auto pr-1.5"
          style={{ width: railWidth }}
          aria-label="Groups"
        >
          {groups.map((g) => {
            const isActive = g.key === active.key
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setActiveKey(g.key)}
                aria-current={isActive}
                className={`flex w-full items-center gap-2 rounded-[8px] border px-2.5 py-[7px] text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/60 ${
                  isActive
                    ? 'border-gis-navy/25 bg-[#eef3fb]'
                    : 'border-transparent hover:bg-[#f5f7fa]'
                }`}
              >
                <span
                  className={`min-w-0 flex-1 truncate text-[12px] font-semibold ${
                    isActive ? 'text-gis-navy' : 'text-gis-ink-2'
                  }`}
                >
                  {g.label}
                </span>
                <span className="flex-none rounded-[6px] bg-gis-track px-[6px] py-[1px] text-[10px] font-bold tabular-nums text-gis-ink-2">
                  {g.rows.length}
                </span>
              </button>
            )
          })}
        </nav>

        {/* Draggable divider. */}
        <SplitHandle onPointerDown={onHandlePointerDown} />

        {/* Right pane — the active group's rows (image boxes in Gallery, rows in List). */}
        <div className="@container/list ml-1.5 min-h-0 min-w-0 flex-1 overflow-y-auto rounded-[10px] border border-gis-line-2 bg-[#fcfdfe]">
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gis-line-2 bg-white/95 px-3 py-[7px] backdrop-blur">
            <span className="min-w-0 truncate text-[12px] font-bold text-gis-ink">{active.label}</span>
            <span className="flex-none rounded-[6px] bg-gis-track px-[7px] py-[1px] text-[10.5px] font-bold tabular-nums text-gis-ink-2">
              {active.rows.length}
            </span>
          </div>
          <div className="p-2">{renderGroup(active.rows)}</div>
        </div>
      </div>
    </div>
  )
}
